import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { QrCode } from "@bw/shared/components";
import { useCheckinPassSecondsRemaining } from "@bw/shared/hooks";
import { formatCheckinPassCountdown } from "@bw/shared/utils";

export interface MaterialCheckinPassQRProps {
  token: string;
  expiresAt: string;
  label?: string;
  onRefresh?: () => void;
}

/** Player-native MUI presentation for the shared single-use QR contract. */
export function MaterialCheckinPassQR({
  token,
  expiresAt,
  label,
  onRefresh,
}: MaterialCheckinPassQRProps) {
  const remaining = useCheckinPassSecondsRemaining(expiresAt);
  const expired = remaining <= 0;

  return (
    <Stack
      role="group"
      aria-label="Check-in pass"
      spacing={2}
      sx={{ alignItems: "center", width: "100%", textAlign: "center" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          {label ?? "Check-in pass"}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label="Single-use · staff scan once"
        />
      </Stack>

      {expired ? (
        <Stack spacing={1.5} role="alert" sx={{ alignItems: "center", py: 2 }}>
          <Typography color="text.secondary">
            This check-in code expired.
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => onRefresh?.()}
          >
            Refresh
          </Button>
        </Stack>
      ) : (
        <>
          <Box
            role="img"
            aria-label="One-time check-in QR code"
            sx={{
              maxWidth: "100%",
              bgcolor: "common.white",
              borderRadius: 1,
              overflow: "hidden",
              "& svg": {
                display: "block",
                width: "min(264px, 100%)",
                height: "auto",
              },
            }}
          >
            <QrCode value={token} size={264} />
          </Box>
          <Typography
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            Expires in {formatCheckinPassCountdown(remaining)}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => onRefresh?.()}>
            Refresh
          </Button>
        </>
      )}
    </Stack>
  );
}
